// Structured logging utility for StayProperties frontend
// Usage: const logger = createLogger('ComponentName');
//        logger.info('message', { optional: 'context' });

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface Logger {
  debug(message: string, context?: Record<string, unknown>): void;
  info(message: string, context?: Record<string, unknown>): void;
  warn(message: string, context?: Record<string, unknown>): void;
  error(message: string, error?: unknown, context?: Record<string, unknown>): void;
}

const LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Auto-detect from Vite environment, default to 'warn' in production
let currentLevel: LogLevel =
  typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'production'
    ? 'warn'
    : 'debug';

export function setLogLevel(level: LogLevel): void {
  currentLevel = level;
}

export function getLogLevel(): LogLevel {
  return currentLevel;
}

function shouldLog(level: LogLevel): boolean {
  return LEVEL_PRIORITY[level] >= LEVEL_PRIORITY[currentLevel];
}

export function createLogger(namespace: string): Logger {
  const prefix = `[${namespace}]`;

  return {
    debug(message: string, context?: Record<string, unknown>) {
      if (!shouldLog('debug')) return;
      if (context) { console.log(prefix, message, context); } else { console.log(prefix, message); }
    },

    info(message: string, context?: Record<string, unknown>) {
      if (!shouldLog('info')) return;
      if (context) { console.log(prefix, message, context); } else { console.log(prefix, message); }
    },

    warn(message: string, context?: Record<string, unknown>) {
      if (!shouldLog('warn')) return;
      if (context) { console.warn(prefix, message, context); } else { console.warn(prefix, message); }
    },

    error(message: string, error?: unknown, context?: Record<string, unknown>) {
      if (!shouldLog('error')) return;
      if (error && context) {
        console.error(prefix, message, error, context);
      } else if (error) {
        console.error(prefix, message, error);
      } else {
        console.error(prefix, message);
      }
    },
  };
}
